import React from "react";
import { STYLES, C, Section, Consult } from "./ffs/FfsSubpage";
import { ABOUT_PATH } from "./AboutDrOvadia";

/* ─── A provider's page ───
   One template for every provider in src/providers.js: Peyton Olson today, any PA, NP, nurse or second
   surgeon who joins later. The content comes entirely from the provider's entry; nothing here is specific
   to one person. Rendered at /team/<slug> by ProviderRoute in src/App.js.

   Nothing in this file may read `window` or `document` while rendering, or the publish-time render breaks. */

const EXTRA_STYLES = `
  .pv-hero-grid { display: grid; grid-template-columns: 1fr 280px; gap: clamp(32px, 5vw, 64px); align-items: end; }
  .pv-photo { margin: 0; }
  .pv-photo img { width: 100%; height: auto; display: block; border-top: 3px solid ${C.gold}; }
  .pv-initials { aspect-ratio: 4 / 5; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); border-top: 3px solid ${C.gold}; font-family: var(--head); font-size: clamp(56px, 8vw, 88px); color: ${C.gold}; letter-spacing: 4px; }
  .pv-role { font-family: var(--body); font-size: 13px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: ${C.gold}; margin-bottom: 14px; }
  .pv-two { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(28px, 4vw, 48px); align-items: start; }
  .pv-list { list-style: none; margin-top: 8px; }
  .pv-list li { font-family: var(--body); font-size: 15.5px; line-height: 1.55; color: ${C.textBody}; padding: 12px 0; border-top: 1px solid ${C.border}; }
  .pv-list li span { display: block; color: ${C.textLight}; font-size: 13.5px; margin-top: 2px; }
  .pv-list li a { color: ${C.navy}; font-weight: 600; text-decoration: underline; text-decoration-color: ${C.gold}; text-decoration-thickness: 1.5px; text-underline-offset: 4px; }
  .pv-sub { font-family: var(--body); font-size: 12.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: ${C.goldDark}; margin: 0 0 6px; }
  .pv-sub + .pv-sub, .pv-list + .pv-sub { margin-top: 32px; }
  @media (max-width: 860px) {
    .pv-hero-grid, .pv-two { grid-template-columns: 1fr; }
    .pv-photo { max-width: 240px; }
  }
`;

export default function ProviderPage({ provider: p }) {
  const initials = `${p.firstName[0]}${p.lastName[0]}`;
  const rest = p.bio.slice(1);
  return (
    <main id="main-content" className="fs" style={{ background: C.bg }}>
      <style>{STYLES + EXTRA_STYLES}</style>

      <header className="fs-hero">
        <div className="fs-wrap">
          <div className="pv-hero-grid">
            <div>
              <p className="fs-crumb"><a href={ABOUT_PATH}>About</a> &nbsp;/&nbsp; <a href={`${ABOUT_PATH}#team`}>The team</a></p>
              <h1>{p.name}</h1>
              <div className="fs-stripe" aria-hidden="true"><span style={{ background: C.gold }} /></div>
              <p className="pv-role">{p.role}{p.joined ? ` · joined ${p.joined}` : ""}</p>
              <p className="fs-hero-lede">{p.bio[0]}</p>
              <div className="fs-btns">
                <a className="fs-btn fs-btn-gold" href="#consult">Request a consultation</a>
                <a className="fs-btn fs-btn-line" href={ABOUT_PATH}>About Dr. Ovadia</a>
              </div>
            </div>
            <figure className="pv-photo">
              {p.photo
                ? <img src={p.photo} width="800" height="1000" alt={p.photoAlt || p.name} />
                : <div className="pv-initials" role="img" aria-label={p.name}>{initials}</div>}
            </figure>
          </div>
        </div>
      </header>

      {rest.length > 0 && (
        <Section id="about" eyebrow="About" title={`About ${p.firstName}`} intro={rest} />
      )}

      {p.focus && p.focus.length > 0 && (
        <Section id="role" eyebrow="At the practice" title={`${p.firstName}'s role`} alt narrow={false}>
          <div className={`fs-cards${p.focus.length === 3 ? " fs-cards-3" : ""}`}>
            {p.focus.map((f) => (
              <div key={f.title} className="fs-card"><h3>{f.title}</h3><p>{f.text}</p></div>
            ))}
          </div>
        </Section>
      )}

      <Section id="training" eyebrow="Background" title="Education and credentials">
        <div className="pv-two">
          <div>
            {p.education && p.education.length > 0 && (
              <>
                <p className="pv-sub">Education</p>
                <ul className="pv-list">
                  {p.education.map((e) => <li key={e.what}>{e.what}<span>{e.where}{e.year ? `, ${e.year}` : ""}</span></li>)}
                </ul>
              </>
            )}
            {p.languages && p.languages.length > 0 && (
              <>
                <p className="pv-sub">Languages</p>
                <ul className="pv-list"><li>{p.languages.join(" and ")}</li></ul>
              </>
            )}
          </div>
          <div>
            {p.credentials && p.credentials.length > 0 && (
              <>
                <p className="pv-sub">Certification and licensure</p>
                <ul className="pv-list">
                  {p.credentials.map((c) => <li key={c.name}>{c.name}<span>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.by}</a> : c.by}</span></li>)}
                </ul>
              </>
            )}
            {p.memberships && p.memberships.length > 0 && (
              <>
                <p className="pv-sub">Memberships</p>
                <ul className="pv-list">
                  {p.memberships.map((m) => <li key={m.name}>{m.url ? <a href={m.url} target="_blank" rel="noopener noreferrer">{m.name}</a> : m.name}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
        <p className="fs-note fs-narrow">{p.firstName} works alongside <a href={ABOUT_PATH}>Dr. Steven Ovadia</a>, board certified plastic surgeon, who plans and leads the surgery of every patient at the practice.</p>
      </Section>

      <Consult text={`Depending on your procedure, you may meet with ${p.firstName} as well as Dr. Ovadia along the way. Your surgery is planned with Dr. Ovadia.`} />
    </main>
  );
}
